import _find from 'lodash/find';
import _get from 'lodash/get';
import _flow from 'lodash/flow';
import _cloneDeep from 'lodash/cloneDeep';
import _head from 'lodash/head';
import _last from 'lodash/last';
import _isEmpty from 'lodash/isEmpty';
/**
 * @desc  UtilService class.
 * @ngInject
 */
export default class UtilService {
  /**
   * Constructor of the class.
   *
   * @param {$log}  $log
   * @param {moment}  moment
   */
  constructor(
    $log, moment
  ) {
    this.$log = $log;
    this.moment = moment;

    this.months = [
      {name: 'styczeń', value: 1},
      {name: 'luty', value: 2},
      {name: 'marzec', value: 3},
      {name: 'kwiecień', value: 4},
      {name: 'maj', value: 5},
      {name: 'czerwiec', value: 6},
      {name: 'lipiec', value: 7},
      {name: 'sierpień', value: 8},
      {name: 'wrzesień', value: 9},
      {name: 'październik', value: 10},
      {name: 'listopad', value: 11},
      {name: 'grudzień', value: 12}
    ];
    this.weekdays = [
      {name: 'Poniedziałek', value: 1},
      {name: 'Wtorek', value: 2},
      {name: 'Środa', value: 3},
      {name: 'Czwartek', value: 4},
      {name: 'Piątek', value: 5},
      {name: 'Sobota', value: 6},
      {name: 'Niedziela', value: 7}
    ];
    this.weekdaysMap = {
      monday: this.weekdays[0].name,
      tuesday: this.weekdays[1].name,
      wednesday: this.weekdays[2].name,
      thursday: this.weekdays[3].name,
      friday: this.weekdays[4].name,
      saturday: this.weekdays[5].name,
      sunday: this.weekdays[6].name
    };
    this.weekdaysSortOrder = {
      monday: 0,
      tuesday: 1,
      wednesday: 2,
      thursday: 3,
      friday: 4,
      saturday: 5,
      sunday: 6
    };
  }

  getRoleName(roleId) {
    let roleName = '';
    switch (roleId) {
      case 1:
        roleName = 'EMPTY';
        break;
      case 2:
        roleName = 'MANAGER';
        break;
      case 3:
        roleName = 'HEAD_TEACHER';
        break;
      case 4:
        roleName = 'TEACHER';
        break;
      case 5:
        roleName = 'SUPPLY_TEACHER';
        break;
      default:
        roleName = '';
    }

    return roleName;
  }

  getAllMonths() {
    return this.months;
  }

  getMonthName(month) {
    return (_flow([
      () => _find(this.months, ['value', Number(month)]),
      findMonth => _get(findMonth, 'name', '')
    ]))();
  }

  getAllWeekDays() {
    return this.weekdays;
  }

  getWeekDaysMap() {
    return this.weekdaysMap;
  }

  parseTime(timeString = '') {
    let date = new Date();
    date.setHours(0, 0, 0, 0);

    try {
      if (angular.isNumber(Number(timeString))) {
        date = this.moment()
          .milliseconds(0)
          .seconds(0)
          .minutes(0)
          .hours(Number(timeString))
          .toDate();
      }

      if (angular.isString(timeString)) {
        const time = timeString.split(':');
        for (const pieceOfTime of time) {
          if (isNaN(Number(pieceOfTime))) {
            throw new Error('Invalid time string', timeString);
          }
        }

        switch (time.length) {
          case 1:
            date = this.moment().milliseconds(0).seconds(0).minutes(0).hours(time[0]).toDate();
            break;
          case 2:
            date = this.moment().milliseconds(0).seconds(0).minutes(time[1]).hours(time[0]).toDate();
            break;
          case 3:
            date = this.moment().milliseconds(0).seconds(time[2]).minutes(time[1]).hours(time[0]).toDate();
            break;
          case 4:
            date = this.moment().milliseconds(time[3]).seconds(time[2]).minutes(time[1]).hours(time[0]).toDate();
            break;
          default:
            throw new Error('Invalid time string', timeString);
        }
      }

      return date;
    } catch (error) {
      this.$log.error({error});
      return NaN;
    }
  }

  /**
   * @desc Funkcja bierze jako argument tablicę z datami
   * i zwraca tablicę dat, uzupełnioną o brakujące daty
   * w zakresie.
   * @example
   * // returns ['2019-09-20', '2019-09-21', '2019-09-22']
   * fillDateRange(['2019-09-20', '2019-09-22'])
   * @param {array} dateRange - Array of dates in format 'YYYY-MM-DD'
   * @param {string} resolution - Resolution of date range ['day', 'month']
   * @return {array} Array of dates in format 'YYYY-MM-DD'
   */
  fillDateRange(dateRange = [], resolution = 'day') {
    const dateRangeClone = _cloneDeep(dateRange);
    if (!angular.isArray(dateRangeClone)) {
      throw new Error('Date range is not a array');
    }

    if (_isEmpty(dateRangeClone)) {
      throw new Error('Date range is empty');
    }

    return this.getDateRange(dateRangeClone[0], dateRangeClone[dateRangeClone.length - 1], resolution);
  }

  /**
   * @desc Funkcja przyjmuje jako argumenty dwie daty
   * i zwraca przedział dat ograniczony argumentami
   * @example
   * // returns ['2019-09-20', '2019-09-21', '2019-09-22']
   * getDateRange('2019-09-20', '2019-09-22')
   * @param {string} startDate - Date in format 'YYYY-MM-DD'
   * @param {string} endDate - Date in format 'YYYY-MM-DD'
   * @param {string} resolution - Resolution of date range ['day', 'month']
   * @return {array} Array of dates in format 'YYYY-MM-DD'
   */
  getDateRange(startDate, endDate, resolution = 'day') {
    const dateArray = [];
    let currentDate = this.moment(startDate);
    const stopDate = this.moment(endDate);

    while (currentDate <= stopDate) {
      switch (resolution) {
        case 'day':
          dateArray.push(this.moment(currentDate).format('YYYY-MM-DD'));
          currentDate = this.moment(currentDate).add(1, 'days');
          break;
        case 'month':
          dateArray.push(this.moment(currentDate).format('YYYY-MM'));
          currentDate = this.moment(currentDate).add(1, 'months');
          break;
        default:
          throw new Error(`Unexpected date range resolution: ${resolution}`);
      }
    }

    return dateArray;
  }

  /**
   * @desc Funkcja bierze jako argument tablicę z datami
   * i zwraca nową tablicę z datami dopełnionymi do pełnych
   * tygodni. Uwaga! podany zakres dat musi być ciągły.
   * @example
   * // returns ['2019-09-16', '2019-09-17', '2019-09-18', '2019-09-19', '2019-09-20', '2019-09-21', '2019-09-22']
   * completeWeeks(['2019-09-18', '2019-09-19', '2019-09-20'])
   * @param {array} dateRange - Array of dates in format 'YYYY-MM-DD'
   * @return {array} Array of dates in format 'YYYY-MM-DD'
   */
  completeWeeks(dateRange) {
    if (!angular.isArray(dateRange) || !dateRange.length) {
      return [];
    }

    const dateRangeCopy = _cloneDeep(dateRange);
    const momentDates = dateRangeCopy.map(item => this.moment(item));

    const firstDay = _head(momentDates);
    const daysToSubtract = firstDay.weekday();

    for (let i = 1; i <= daysToSubtract; i++) {
      const cloneFirstDay = this.moment(firstDay);
      cloneFirstDay.subtract(i, 'days');
      momentDates.unshift(cloneFirstDay);
    }

    const lastDay = _last(momentDates);
    const daysToAdd = 6 - lastDay.weekday();

    for (let i = 1; i <= daysToAdd; i++) {
      const cloneLastDay = this.moment(lastDay);
      cloneLastDay.add(i, 'days');
      momentDates.push(cloneLastDay);
    }

    return momentDates.map(item => item.format('YYYY-MM-DD'));
  }

  getFullWeeks(uncompletedWeeks = []) {
    return _flow([
      () => this.fillDateRange(uncompletedWeeks, 'day'),
      data => this.completeWeeks(data)
    ])();
  }

  minutesToString(minutes = 0) {
    return `${Math.floor(minutes / 60)} godz. ${minutes % 60} min.`;
  }
}
